// Login API — OTP login on the live endpoints POST /auth/login/otp/request
// and /auth/login/otp/verify; password login on POST /auth/login. Dev OTP is
// 123456. Login always uses password followed by OTP; applicants use tracking
// until approval and do not enter the customer login flow.
import { ApiError, del, deviceInfo, get, isRealApiEnabled, post } from './client';
import { clearAuthSession, getAccessToken, getRefreshToken, setSession, setTokens } from './session';

export class AuthApiError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'AuthApiError';
    this.code = code;
  }
}

const toAuthError = (err: unknown, fallback: string): AuthApiError => {
  if (err instanceof ApiError) return new AuthApiError(err.code, err.message);
  return new AuthApiError('ERROR', fallback);
};

// ---- identifier classification: email / +91 mobile / customer id ----
const identifierBody = (identifier: string): Record<string, string> => {
  const id = identifier.trim();
  if (id.includes('@')) return { email: id };
  const digits = id.replace(/\D/g, '');
  const mobile = digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
  if (/^[6-9]\d{9}$/.test(mobile)) return { mobileNumber: `+91${mobile}` };
  return { customerId: id };
};

// Mobile masking per §4: first 2, last 3 → "+91 98xxx xx210".
const maskMobile = (mobile: string): string => {
  const digits = mobile.replace(/\D/g, '');
  const local = digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
  if (local.length !== 10) return mobile;
  return `+91 ${local.slice(0, 2)}xxx xx${local.slice(7)}`;
};

// ---- OTP login ----
export interface OtpLoginSession {
  transactionId: string;
  deliveryMethod: string;
  maskedDestination?: string;
  expiresInSeconds?: number;
  requiresPasswordChange?: boolean;
  passwordSetupToken?: string;
}

const normalizeExpirySeconds = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return undefined;
  // The live API currently returns 900000 for a 900-second OTP lifetime.
  return value > 86400 ? Math.ceil(value / 1000) : value;
};

const parseOtpSession = (data: Record<string, unknown>, identifier: string): OtpLoginSession => {
  const body = identifierBody(identifier);
  const maskedDestination = typeof data.maskedDestination === 'string'
    ? data.maskedDestination
    : typeof data.maskedMobile === 'string'
      ? data.maskedMobile
      : body.mobileNumber
        ? maskMobile(body.mobileNumber)
        : undefined;
  return {
    transactionId: String(data.transactionId ?? data.otpTransactionId ?? ''),
    deliveryMethod: String(data.deliveryMethod ?? 'SMS'),
    maskedDestination,
    expiresInSeconds: normalizeExpirySeconds(data.expiresInSeconds ?? data.expiresIn),
    requiresPasswordChange: Boolean(data.requiresPasswordChange),
    passwordSetupToken: typeof data.passwordSetupToken === 'string' ? data.passwordSetupToken : undefined,
  };
};

/**
 * Start the legacy OTP-only login contract. The Phase 5 UI intentionally uses
 * loginWithPassword instead because customer login is password-first.
 */
export const requestLoginOtp = async (identifier: string): Promise<OtpLoginSession> => {
  try {
    const data = await post<Record<string, unknown>>('/auth/login/otp/request', {
      ...identifierBody(identifier),
      deviceInfo: deviceInfo(),
    });
    const session = parseOtpSession(data ?? {}, identifier);
    if (!session.transactionId) throw new AuthApiError('LOGIN_RESPONSE_INVALID', 'Login response missing OTP transaction. Please try again.');
    return session;
  } catch (err) {
    throw normalizeLoginError(err, 'Could not send the OTP. Please try again.');
  }
};

/** @deprecated Use requestLoginOtp for the OTP-only contract. */
export const startOtpLogin = requestLoginOtp;

export const generateLoginOtp = async (session: OtpLoginSession): Promise<OtpLoginSession> => {
  try {
    const data = await post<Record<string, unknown>>('/auth/login/otp/generate', {
      transactionId: session.transactionId,
      deliveryMethod: session.deliveryMethod,
      deviceInfo: { ...deviceInfo(), channel: 'WEB' },
    });
    const refreshed = parseOtpSession(data ?? {}, session.maskedDestination ?? '');
    if (!refreshed.transactionId) throw new AuthApiError('LOGIN_RESPONSE_INVALID', 'OTP resend response was invalid. Please try again.');
    return refreshed;
  } catch (err) {
    throw normalizeLoginError(err, 'Could not resend the OTP. Please try again.');
  }
};

/** @deprecated Use generateLoginOtp with the active OTP session. */
export const resendLoginOtp = async (identifier: string): Promise<OtpLoginSession> => {
  const session = await startOtpLogin(identifier);
  return generateLoginOtp(session);
};

export interface LoginResult {
  sessionType: 'TRACKING' | 'FULL';
  applicationReference?: string;
  customerId?: string;
  customerState?: string;
  requiresPasswordChange?: boolean;
  passwordSetupToken?: string;
}

const completeLogin = (data: {
  accessToken?: string;
  refreshToken?: string;
  customerId?: string;
  userId?: string;
  username?: string;
  sessionType?: string;
  applicationReference?: string;
  customerState?: string;
  mustChangePassword?: boolean;
  passwordChangeRequired?: boolean;
  passwordSetupToken?: string;
}): LoginResult => {
  setTokens(data.accessToken, data.refreshToken);
  const customerId = data.customerId ?? data.userId;
  if (customerId) localStorage.setItem('customerId', customerId);
  const sessionType: LoginResult['sessionType'] =
    data.sessionType?.toUpperCase() === 'TRACKING' ? 'TRACKING' : 'FULL';
  setSession({ type: sessionType, reference: data.applicationReference, customerId });
  const requiresPasswordChange = Boolean(data.mustChangePassword ?? data.passwordChangeRequired);
  // A temporary-password login never returns passwordSetupToken; the access
  // token it returns is the one /auth/password/first-set accepts.
  const setupToken = requiresPasswordChange ? data.passwordSetupToken ?? data.accessToken : data.passwordSetupToken;
  return {
    sessionType,
    applicationReference: data.applicationReference,
    customerId,
    customerState: data.customerState,
    requiresPasswordChange,
    passwordSetupToken: typeof setupToken === 'string' && setupToken ? setupToken : undefined,
  };
};

const loginData = (data: Record<string, unknown>) => parseOtpSession(data, String(data.customerId ?? data.username ?? ''));

const normalizeLoginError = (err: unknown, fallback: string): AuthApiError => {
  if (!(err instanceof ApiError)) return toAuthError(err, fallback);
  if (err.code === 'PASSWORD_INCORRECT' || err.code === 'AUTH_012') {
    return new AuthApiError('PASSWORD_INCORRECT', 'Incorrect password.');
  }
  if (err.code === 'LOGIN_LOCKED') {
    return new AuthApiError('LOGIN_LOCKED', 'Too many attempts. Your login is locked for 24 hours.');
  }
  if (err.code === 'ACCOUNT_NOT_FOUND' || err.code === 'ERR_003') {
    return new AuthApiError('ACCOUNT_NOT_FOUND', "We couldn't find an account for that email, Customer ID or mobile number.");
  }
  return new AuthApiError(err.code, err.message);
};

export const verifyLoginOtp = async (
  session: OtpLoginSession,
  code: string
): Promise<LoginResult> => {
  if (!isRealApiEnabled()) {
    if (session.transactionId.startsWith('mock-login-')) {
      if (code !== sessionStorage.getItem(MOCK_OTP_KEY)) throw new AuthApiError('AUTH_012', 'The code you entered is incorrect. Please try again.');
      const requiresPasswordChange = Boolean(session.requiresPasswordChange);
      if (requiresPasswordChange) return { sessionType: 'FULL', customerId: 'CUS-MOCK-001', customerState: 'ACTIVE', requiresPasswordChange: true, passwordSetupToken: session.passwordSetupToken };
      // Use the same sentinel recognized by the legacy account service so its
      // mock profile/account calls do not treat this session as unauthorized.
      setTokens('nova-mock-dev-token');
      localStorage.setItem('customerId', 'CUS-MOCK-001');
      setSession({ type: 'FULL', customerId: 'CUS-MOCK-001', identifier: sessionStorage.getItem(MOCK_IDENTIFIER_KEY) ?? '' });
      return { sessionType: 'FULL', customerId: 'CUS-MOCK-001', customerState: 'ACTIVE' };
    }
  }
  try {
    const data = await post<Parameters<typeof completeLogin>[0]>('/auth/login/otp/verify', {
      transactionId: session.transactionId,
      code,
      deliveryMethod: session.deliveryMethod,
      deviceInfo: { ...deviceInfo(), channel: 'WEB' },
    });
    const result = completeLogin(data ?? {});
    if (result.requiresPasswordChange) {
      return result;
    }
    const profile = await get<Record<string, unknown>>('/users/me');
    const customerId = String(profile.customerId ?? profile.id ?? result.customerId ?? '');
    if (customerId) localStorage.setItem('customerId', customerId);
    const customerState = String(profile.customerState ?? profile.state ?? result.customerState ?? 'ACTIVE').toUpperCase();
    setSession({ type: 'FULL', customerId, identifier: customerId });
    return { ...result, customerId, customerState };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.code === 'AUTH_012') {
        throw new AuthApiError('AUTH_012', 'The code you entered is incorrect. Please try again.');
      }
      if (err.code === 'AUTH_017') {
        throw new AuthApiError('AUTH_017', 'That code has expired. Please resend a new one.');
      }
      if (err.code === 'AUTH_020') {
        throw new AuthApiError('AUTH_020', 'Too many attempts. Please resend the code.');
      }
      throw new AuthApiError(err.code, err.message);
    }
    throw toAuthError(err, 'OTP verification failed. Please try again.');
  }
};

// Login is always password-first in Phase 5. Applicants use tracking and never
// enter this customer login flow before approval.

export const loginWithPassword = async (identifier: string, password: string): Promise<OtpLoginSession> => {
  if (!isRealApiEnabled()) return mockLogin(identifier, password);
  try {
    const raw = await post<Record<string, unknown>>('/auth/login', {
      ...identifierBody(identifier),
      // A password pasted from an email often carries a space at either end.
      password: password.trim(),
      deliveryMethod: 'EMAIL',
      deviceInfo: { ...deviceInfo(), channel: 'WEB' },
    });
    const data = loginData(raw ?? {});
    if (!data.transactionId) throw new AuthApiError('LOGIN_RESPONSE_INVALID', 'Login response missing OTP transaction. Please try again.');
    return data;
  } catch (err) {
    if (err instanceof AuthApiError) throw err;
    throw normalizeLoginError(err, 'Login failed. Please try again.');
  }
};

export interface FirstLoginPasswordInput {
  temporaryPassword: string;
  newPassword: string;
  confirmPassword: string;
}

let restrictedPasswordToken = '';
let loginPrefillEmail = '';
let loginNotice = '';
const TEMP_PASSWORD_VALUE_KEY = 'nova_temp_password_value';
const TEMP_PASSWORD_EMAIL_KEY = 'nova_temp_password_email';
const TEMP_PASSWORD_EXPIRES_KEY = 'nova_temp_password_expires';
const TEMP_PASSWORD_ATTEMPTS_KEY = 'nova_temp_password_attempts';
const TEMP_PASSWORD_RETIRED_KEY = 'nova_temp_password_retired';
const MOCK_PASSWORD_KEY = 'nova_mock_password';
const MOCK_OTP_KEY = 'nova_mock_login_otp';
const MOCK_IDENTIFIER_KEY = 'nova_mock_login_identifier';

const randomTemporaryPassword = (): string => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#';
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
};

const issueMockTemporaryPassword = (email = 'customer@novabank.test'): string => {
  const value = randomTemporaryPassword();
  sessionStorage.setItem(TEMP_PASSWORD_VALUE_KEY, value);
  sessionStorage.setItem(TEMP_PASSWORD_EMAIL_KEY, email);
  sessionStorage.setItem(TEMP_PASSWORD_EXPIRES_KEY, String(Date.now() + 72 * 60 * 60 * 1000));
  sessionStorage.setItem(TEMP_PASSWORD_ATTEMPTS_KEY, '0');
  sessionStorage.removeItem(TEMP_PASSWORD_RETIRED_KEY);
  return value;
};

export const getMockTemporaryPassword = (): string => sessionStorage.getItem(TEMP_PASSWORD_VALUE_KEY) ?? '';

export const issueTemporaryPassword = (email = ''): string => {
  if (isRealApiEnabled()) throw new AuthApiError('NOT_AVAILABLE', 'Temporary password service is not available yet.');
  return issueMockTemporaryPassword(email || sessionStorage.getItem(TEMP_PASSWORD_EMAIL_KEY) || 'customer@novabank.test');
};

export const resendTemporaryPassword = async (email: string, dateOfBirth: string): Promise<void> => {
  if (!isRealApiEnabled()) {
    issueMockTemporaryPassword(email);
    return;
  }
  await post('/auth/password/temporary/resend', { email, dateOfBirth });
};

export interface PasswordRecoverySession {
  transactionId: string;
  maskedDestination?: string;
}

export const startPasswordRecovery = async (input: {
  customerId?: string;
  mobileNumber?: string;
  email?: string;
}): Promise<PasswordRecoverySession> => {
  if (!isRealApiEnabled()) return { transactionId: `mock-password-${Date.now()}`, maskedDestination: '****7890' };
  const data = await post<Record<string, unknown>>('/auth/password/forgot', input);
  const transactionId = String(data.transactionId ?? '');
  if (!transactionId) throw new AuthApiError('PASSWORD_RECOVERY_INVALID', 'Password recovery could not be started.');
  return {
    transactionId,
    maskedDestination: typeof data.maskedDestination === 'string' ? data.maskedDestination : undefined,
  };
};

export const resetPassword = async (input: {
  transactionId: string;
  code: string;
  newPassword: string;
}): Promise<void> => {
  if (!isRealApiEnabled()) return;
  await post('/auth/password/reset', input);
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  if (!isRealApiEnabled()) return;
  await post('/auth/password/change', { currentPassword, newPassword, deviceInfo: deviceInfo() });
};

export interface StepUpOtpInput {
  /** A fresh UUID is minted when this is left out. */
  transactionId?: string;
  transactionType: string;
  amount?: number;
  destination?: string;
  source?: string;
  /** Defaults to 'AUTH'. */
  channel?: string;
  /** Defaults to 'SMS'. */
  deliveryMethod?: string;
  purpose?: string;
}

export interface StepUpOtpSession {
  transactionId: string;
  deliveryMethod: string;
  expiresInSeconds?: number;
  maskedDestination?: string;
}

/**
 * Sends a step-up OTP. The server refuses the request unless transactionId,
 * transactionType, channel and deliveryMethod are all present. Use the
 * transactionId this returns: the server may issue its own.
 */
export const generateStepUpOtp = async (input: StepUpOtpInput): Promise<StepUpOtpSession> => {
  const transactionId = input.transactionId || crypto.randomUUID();
  const deliveryMethod = input.deliveryMethod ?? 'SMS';
  const data = await post<Record<string, unknown>>('/auth/otp/generate', {
    ...input,
    transactionId,
    transactionType: input.transactionType,
    channel: input.channel ?? 'AUTH',
    deliveryMethod,
    deviceInfo: { ...deviceInfo(), channel: 'WEB' },
  });
  const body = data ?? {};
  return {
    transactionId: String(body.transactionId ?? body.otpTransactionId ?? transactionId),
    deliveryMethod: String(body.deliveryMethod ?? deliveryMethod),
    expiresInSeconds: normalizeExpirySeconds(body.expiresInSeconds ?? body.expiresIn),
    maskedDestination: typeof body.maskedDestination === 'string' ? body.maskedDestination : undefined,
  };
};

const TOO_MANY_REQUESTS = 'Too many requests. Wait a minute and try again.';

const STEP_UP_OTP_MESSAGES: Record<string, string> = {
  OTP_001: 'That code is wrong or has expired. Check it and try again.',
  OTP_003: 'That code has expired. Send a new code.',
  OTP_002: 'Too many wrong tries. Send a new code.',
  OTP_006: 'Too many wrong tries. Send a new code.',
  OTP_005: 'A code was sent a moment ago. Wait a little before asking for another.',
  RATE_001: TOO_MANY_REQUESTS,
  HTTP_429: TOO_MANY_REQUESTS,
};

/**
 * Gives a step-up OTP error (OTP_*, RATE_001 or HTTP 429) a friendly message
 * and keeps its code. Any other error comes back unchanged.
 */
export const toFriendlyOtpError = (err: unknown): unknown => {
  if (!(err instanceof ApiError)) return err;
  const message = STEP_UP_OTP_MESSAGES[err.code] ?? (err.status === 429 ? TOO_MANY_REQUESTS : undefined);
  if (!message) return err;
  return new ApiError(err.code, message, err.status, err.correlationId, err.fieldErrors);
};

export const verifyStepUpOtp = async (input: {
  transactionId: string;
  code: string;
  transactionType: string;
  deliveryMethod: string;
  amount?: number;
  purpose?: string;
}) => post<Record<string, unknown>>('/auth/otp/verify', {
  ...input,
  deviceInfo: { ...deviceInfo(), channel: 'WEB' },
});

const mockLogin = (identifier: string, password: string): OtpLoginSession => {
  const temporary = sessionStorage.getItem(TEMP_PASSWORD_RETIRED_KEY) === '1' ? '' : getMockTemporaryPassword();
  const expires = Number(sessionStorage.getItem(TEMP_PASSWORD_EXPIRES_KEY) || 0);
  const attempts = Number(sessionStorage.getItem(TEMP_PASSWORD_ATTEMPTS_KEY) || 0);
  if (temporary && Date.now() > expires) throw new AuthApiError('TEMP_PASSWORD_EXPIRED', 'Your temporary password has expired. Request a new one.');
  if (temporary && attempts >= 3) throw new AuthApiError('TEMP_PASSWORD_LOCKED', 'Your temporary password is locked. Request a new one.');
  const expectedPassword = sessionStorage.getItem(MOCK_PASSWORD_KEY) ?? 'Password@1';
  const usingTemporary = Boolean(temporary && password === temporary);
  if (password !== expectedPassword && !usingTemporary) {
    if (temporary) sessionStorage.setItem(TEMP_PASSWORD_ATTEMPTS_KEY, String(attempts + 1));
    throw new AuthApiError('PASSWORD_INCORRECT', 'Incorrect password.');
  }
  const transactionId = `mock-login-${Date.now()}`;
  sessionStorage.setItem(MOCK_OTP_KEY, '123456');
  sessionStorage.setItem(MOCK_IDENTIFIER_KEY, identifier.trim());
  return { transactionId, deliveryMethod: 'SMS', maskedDestination: '+91 98xxx xx210', expiresInSeconds: 180, requiresPasswordChange: usingTemporary, passwordSetupToken: usingTemporary ? `mock-setup-${Date.now()}` : undefined };
};

export const setFirstLoginPassword = async (input: FirstLoginPasswordInput): Promise<void> => {
  if (input.newPassword !== input.confirmPassword) {
    throw new AuthApiError('PASSWORD_MISMATCH', 'Passwords do not match.');
  }
  const email = sessionStorage.getItem(TEMP_PASSWORD_EMAIL_KEY) ?? '';
  if (input.newPassword === input.temporaryPassword) {
    throw new AuthApiError('PASSWORD_REUSED', 'Your new password must be different from the temporary password.');
  }
  if (email && input.newPassword.toLowerCase().includes(email.split('@')[0].toLowerCase())) {
    throw new AuthApiError('PASSWORD_CONTAINS_EMAIL', 'Your password cannot contain your email name.');
  }
  if (isRealApiEnabled()) {
    const token = getTemporaryPasswordToken() || getAccessToken();
    if (!token) throw new AuthApiError('SESSION_EXPIRED', 'Sign in again with your temporary password.');
    await post('/auth/password/first-set', {
      newPassword: input.newPassword,
    }, { headers: { Authorization: `Bearer ${token}` } });
    // First-set revokes the temporary session on the server.
    clearAuthSession();
  } else {
    const storedTemporary = getMockTemporaryPassword();
    if (storedTemporary && storedTemporary !== input.temporaryPassword) {
      throw new AuthApiError('TEMP_PASSWORD_INCORRECT', 'That temporary password is incorrect.');
    }
    sessionStorage.setItem(MOCK_PASSWORD_KEY, input.newPassword);
    sessionStorage.removeItem(TEMP_PASSWORD_VALUE_KEY);
    sessionStorage.setItem(TEMP_PASSWORD_RETIRED_KEY, '1');
  }
  clearTemporaryPasswordToken();
  sessionStorage.removeItem(TEMP_PASSWORD_EXPIRES_KEY);
  sessionStorage.removeItem(TEMP_PASSWORD_ATTEMPTS_KEY);
};

export const saveTemporaryPasswordToken = (token: string): void => {
  restrictedPasswordToken = token;
};

export const getTemporaryPasswordToken = (): string => restrictedPasswordToken;

export const clearTemporaryPasswordToken = (): void => {
  restrictedPasswordToken = '';
};

export const setLoginNotice = (email: string, notice: string): void => {
  loginPrefillEmail = email;
  loginNotice = notice;
};

export const consumeLoginNotice = (): { email: string; notice: string } => {
  const value = { email: loginPrefillEmail, notice: loginNotice };
  loginPrefillEmail = '';
  loginNotice = '';
  return value;
};

/**
 * Sets the MPIN, or replaces the current one, and clears an MPIN lock. The
 * code is a step-up OTP sent with transactionType MPIN_SET; the server checks
 * it here, with no separate verify call. The live server takes 4 digits.
 */
export const setMpin = async (mpin: string, otpTransactionId: string, code: string): Promise<void> => {
  if (!isRealApiEnabled()) return;
  await post('/auth/mpin/set', { mpin, otpTransactionId, code });
};

export const logoutEverywhere = (): void => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  post('/auth/logout-everywhere', { accessToken, refreshToken, deviceInfo: deviceInfo() }).catch(() => undefined);
  logout();
};

export const logout = (): void => {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  post('/auth/logout', { accessToken, refreshToken, deviceInfo: deviceInfo() }).catch(() => undefined);
  clearAuthSession();
};

export interface UserSession {
  id: number;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  lastAccessedAt: string;
  expiresAt: string;
  absoluteExpiresAt: string;
  current: boolean;
}

export interface UserDevice {
  id: number;
  deviceFingerprint: string;
  deviceName: string;
  deviceType: string;
  operatingSystem: string;
  browser: string;
  status: string;
  lastUsedAt: string;
}

export interface LoginHistoryPage {
  totalPages: number;
  totalElements: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  size: number;
  number: number;
  content: Record<string, unknown>[];
}

export const listUserSessions = (): Promise<UserSession[]> =>
  isRealApiEnabled()
    ? get<UserSession[]>('/users/me/sessions')
    : Promise.resolve([]);

export const listUserDevices = (): Promise<UserDevice[]> =>
  isRealApiEnabled()
    ? get<UserDevice[]>('/users/me/devices')
    : Promise.resolve([]);

export const trustUserDevice = (id: number): Promise<void> =>
  isRealApiEnabled()
    ? post<void>(`/users/me/devices/${encodeURIComponent(id)}/trust`)
    : Promise.resolve();

export const revokeUserSession = (id: number): Promise<void> =>
  isRealApiEnabled()
    ? del<void>(`/users/me/sessions/${encodeURIComponent(id)}`)
    : Promise.resolve();

export const revokeUserDevice = (id: number): Promise<void> =>
  isRealApiEnabled()
    ? del<void>(`/users/me/devices/${encodeURIComponent(id)}`)
    : Promise.resolve();

export const disableNetBanking = (): Promise<Record<string, unknown>> =>
  isRealApiEnabled()
    ? del<Record<string, unknown>>('/users/me/net-banking')
    : Promise.resolve({});

export const getLoginHistory = (page = 0, size = 20, sort = 'timestamp,DESC'): Promise<LoginHistoryPage> =>
  isRealApiEnabled()
    ? get<LoginHistoryPage>('/users/me/login-history', { params: { page, size, sort } })
    : Promise.resolve({
        totalPages: 0,
        totalElements: 0,
        first: true,
        last: true,
        numberOfElements: 0,
        size,
        number: page,
        content: [],
      });
